import { doc, onSnapshot } from 'firebase/firestore';
import { useState, useEffect } from 'react';
import { db } from '../firebase';

interface UseFirebaseCartsResult<T> {
  data: T | null;
  loading: boolean;
  error: Error | null;
}

/**
 * Custom hook to fetch a single cart by ID in real-time from Firebase Firestore
 * @param cartId - The cart ID to fetch
 * @returns Object containing data, loading state, and error
 */
export const useFirebaseCartById = (
  cartId: string
): UseFirebaseCartsResult<any> => {
  const [data, setData] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!cartId) {
      setLoading(false);
      return;
    }

    setLoading(true);

    // Reference to specific cart document
    const cartDocRef = doc(db, 'Carts', cartId);

    // Set up real-time listener for single document
    const unsubscribe = onSnapshot(
      cartDocRef,
      (docSnap) => {
        if (docSnap.exists()) {
          setData({
            id: docSnap.id,
            ...docSnap.data()
          });
          setLoading(false);
          setError(null);
        } else {
          setData(null);
          setLoading(false);
          setError(new Error('Cart not found'));
        }
      },
      (err) => {
        console.error('❌ Error fetching cart by ID:', err);
        console.error('  Error code:', err.code);
        console.error('  Error message:', err.message);
        setError(err as Error);
        setLoading(false);
      }
    );

    // Cleanup listener on unmount
    return () => {
      unsubscribe();
    };
  }, [cartId]);

  return { data, loading, error };
};
